// ─────────────────────────────────────────────────────────────
// store-sheets.jsx — Shared Settings-Bausteine + Store-Modals.
// Extrahiert aus app.jsx damit flea.jsx + market.jsx diese importieren
// können ohne zirkuläre Abhängigkeiten zu app.jsx.
//
// Exports:
//   Settings-Bausteine:  _SettingsRowFrame, SettingsLinkRow, SettingsToggleRow
//                        SettingsSectionLabel, SettingsCard, SettingsRow, SettingsToggle
//   Store-Modals:        ConfirmDeleteStoreSheet, ModeMembersSheet,
//                        InviteCodeSheet, ConfirmDialog
//   CSV-Export:          exportSingleStoreCsv
// ─────────────────────────────────────────────────────────────
import { useState, useEffect } from 'react';
import { F, T, haptic } from './theme.jsx';
import { useModalViewport, modalShellStyle, ModalHeader } from './modal-utils.jsx';

// BottomSheet wird lazy importiert um Circular-Dep zu vermeiden —
// es liegt in widgets.jsx das seinerseits nichts aus store-sheets importiert.
// Da store-sheets.jsx NACH widgets.jsx im Modul-Graphen steht, ist der
// direkte Import sicher.
import { BottomSheet } from './widgets.jsx';

// ─────────────────────────────────────────────────────────────
// Settings-Bausteine
// ─────────────────────────────────────────────────────────────

export function _SettingsRowFrame({ icon, accent, label, sub, right, onClick, t, dark, F: _F, first, labelColor }) {
  const font = _F || F;
  const interactive = typeof onClick === "function";
  return (
    <button
      onClick={onClick}
      disabled={!interactive}
      style={{
        width: "100%", border: "none", background: "none", cursor: interactive ? "pointer" : "default",
        padding: "13px 16px",
        display: "flex", alignItems: "center", gap: 10,
        fontFamily: font, textAlign: "left",
        borderTop: first ? "none" : `0.5px solid ${t.divider}`,
        transition: "background 0.15s ease",
      }}
      onPointerEnter={interactive ? e => { e.currentTarget.style.background = dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)"; } : undefined}
      onPointerLeave={interactive ? e => { e.currentTarget.style.background = "transparent"; } : undefined}
    >
      {icon && (
        <span style={{
          color: accent || t.textMuted,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>{typeof icon === "function" ? icon(16) : icon}</span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, color: labelColor || t.text, fontWeight: 500, letterSpacing: "-0.1px" }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2, lineHeight: 1.3 }}>{sub}</div>}
      </div>
      {right}
    </button>
  );
}

export function SettingsLinkRow({ icon, accent, label, sub, rightText, onClick, accentLabel, labelColor, t, dark, F: _F, first }) {
  return (
    <_SettingsRowFrame
      icon={icon} accent={accent} label={label} sub={sub}
      labelColor={accentLabel ? (labelColor || t.accent.blue.solid) : labelColor}
      onClick={onClick} t={t} dark={dark} F={_F || F} first={first}
      right={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          {rightText && <span style={{ fontSize: 13, color: t.textMuted, fontVariantNumeric: "tabular-nums" }}>{rightText}</span>}
          <span style={{ color: t.chevron, fontSize: 18, fontWeight: 200 }}>›</span>
        </span>
      }
    />
  );
}

export function SettingsToggleRow({ icon, accent, label, sub, value, onChange, activeColor, t, dark, F: _F, first }) {
  return (
    <_SettingsRowFrame
      icon={icon} accent={value ? (activeColor || accent) : t.textMuted}
      label={label} sub={sub}
      onClick={onChange}
      t={t} dark={dark} F={_F || F} first={first}
      right={
        <span style={{
          width: 46, height: 28, borderRadius: 14, position: "relative",
          background: value ? (activeColor || t.accent.green.solid) : (dark ? "rgba(120,120,128,0.32)" : "rgba(120,120,128,0.18)"),
          boxShadow: value ? "inset 0 1px 2px rgba(0,0,0,0.18)" : "inset 0 1px 2px rgba(0,0,0,0.08)",
          display: "inline-block", flexShrink: 0, transition: "background 0.2s ease",
        }}>
          <span style={{
            position: "absolute", top: 2, left: value ? 20 : 2,
            width: 24, height: 24, borderRadius: "50%", background: "#fff",
            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            transition: "left 0.2s cubic-bezier(0.32,0.72,0,1)",
          }}/>
        </span>
      }
    />
  );
}

export function SettingsSectionLabel({ children, t }) {
  return (
    <div style={{
      fontSize: 13, fontWeight: 600, color: t.textMuted,
      letterSpacing: "0.06em", textTransform: "uppercase",
      paddingLeft: 32, paddingRight: 16,
      paddingTop: 24, paddingBottom: 8,
    }}>{children}</div>
  );
}

export function SettingsCard({ children, dark, t }) {
  return (
    <div style={{
      marginLeft: 16, marginRight: 16,
      background: dark ? "#1c1c1e" : "#ffffff",
      borderRadius: 12,
      overflow: "hidden",
    }}>{children}</div>
  );
}

export function SettingsRow({ label, value, sub, onClick, destructive, isFirst, t, dark, F: _F }) {
  return (
    <button onClick={onClick} disabled={!onClick} style={{
      boxSizing: "border-box",
      width: "100%", padding: "14px 16px",
      background: "transparent", border: "none",
      color: destructive ? t.accent.red.solid : t.text,
      fontFamily: _F || F, fontWeight: 400, fontSize: 15,
      cursor: onClick ? "pointer" : "default", textAlign: "left",
      display: "flex", alignItems: "center", gap: 12,
      borderTop: isFirst ? "none" : `0.5px solid ${t.divider}`,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</div>
        {sub && <div style={{ fontSize: 12, color: t.textSub, marginTop: 2, fontWeight: 400 }}>{sub}</div>}
      </div>
      {value !== undefined && <div style={{ color: destructive ? t.accent.red.solid : t.textMuted, fontSize: 14, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{value}</div>}
      {onClick && !destructive && <div style={{ fontSize: 18, color: t.chevron, fontWeight: 300, flexShrink: 0 }}>›</div>}
    </button>
  );
}

export function SettingsToggle({ label, sub, checked, onChange, isFirst, t, dark, F: _F }) {
  return (
    <div style={{
      boxSizing: "border-box",
      width: "100%", padding: "12px 16px",
      borderTop: isFirst ? "none" : `0.5px solid ${t.divider}`,
      display: "flex", alignItems: "center", gap: 12,
      minHeight: 52,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 400, color: t.text }}>{label}</div>
        {sub && <div style={{ fontSize: 12, color: t.textSub, marginTop: 2 }}>{sub}</div>}
      </div>
      <button onClick={() => { onChange(!checked); haptic(8); }} aria-pressed={checked} style={{
        width: 51, height: 31, borderRadius: 16,
        background: checked ? t.accent.green.solid : (dark ? "rgba(120,120,128,0.32)" : "rgba(120,120,128,0.16)"),
        border: "none", cursor: "pointer", padding: 2,
        position: "relative", transition: "background 0.2s ease",
        flexShrink: 0,
      }}>
        <span style={{
          display: "block", width: 27, height: 27, borderRadius: "50%",
          background: "#fff",
          transform: checked ? "translateX(20px)" : "translateX(0)",
          transition: "transform 0.2s cubic-bezier(0.32, 0.72, 0, 1)",
          boxShadow: "0 3px 8px rgba(0,0,0,0.15), 0 1px 2px rgba(0,0,0,0.1)",
        }}/>
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Store-Modals
// ─────────────────────────────────────────────────────────────

export function ConfirmDeleteStoreSheet({ dark, t, storeName, storeType, onCancel, onConfirm }) {
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const expected = "LÖSCHEN";
  const ready = confirmText.trim().toUpperCase() === expected;
  const typeLabel = storeType === "flea" ? "Trödel-Bereich" : storeType === "market" ? "Standverkauf-Bereich" : "Online-Store";

  return (
    <>
      <div onClick={onCancel} style={{
        position: "fixed", inset: 0, zIndex: 96,
        background: dark ? "rgba(0,0,0,0.65)" : "rgba(0,0,0,0.4)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 420, radius: 24, padding: "22px 22px 18px", zIndex: 97 })}>
        <ModalHeader t={t} dark={dark} F={F} title={`${typeLabel} löschen`} subtitle={storeName} onClose={onCancel} marginBottom={14} />
        <div style={{ background: t.accent.red.weak, color: t.accent.red.solid, borderRadius: 10, padding: "10px 12px", marginBottom: 14, fontSize: 12, lineHeight: 1.5, fontWeight: 500 }}>
          Alle Daten dieses Bereichs werden permanent gelöscht: Bestellungen, Items, Markt-Tage, Verkäufe, Mitgliedschaften. Diese Aktion ist unwiderruflich.
        </div>
        <div style={{ fontSize: 12, color: t.textSub, marginBottom: 6, paddingLeft: 4 }}>
          Tippe <b style={{ color: t.text }}>{expected}</b> zum Bestätigen:
        </div>
        <div style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 12, padding: "0 14px", marginBottom: 14, border: `1px solid ${t.glassBorder}` }}>
          <input autoFocus type="text" value={confirmText} onChange={e => setConfirmText(e.target.value)}
            style={{ width: "100%", background: "transparent", border: "none", outline: "none", padding: "12px 0", fontSize: 14, color: t.text, fontFamily: F, minWidth: 0, letterSpacing: "0.04em" }}/>
        </div>
        <button onClick={async () => { if (!ready || submitting) return; setSubmitting(true); await onConfirm(); setSubmitting(false); }} disabled={!ready || submitting} style={{
          width: "100%", padding: "13px",
          background: ready ? t.accent.red.solid : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"),
          color: ready ? "#fff" : t.textMuted,
          border: "none", borderRadius: 12,
          fontFamily: F, fontWeight: 700, fontSize: 14,
          cursor: ready && !submitting ? "pointer" : "not-allowed",
          marginBottom: 8,
        }}>{submitting ? "Lösche…" : "Endgültig löschen"}</button>
        <button onClick={onCancel} style={{
          width: "100%", padding: "10px",
          background: "transparent", border: "none",
          color: t.textMuted, fontFamily: F, fontWeight: 500, fontSize: 13, cursor: "pointer",
        }}>Abbrechen</button>
      </div>
    </>
  );
}

export function InviteCodeSheet({ dark, t, code, expiresAt, storeName, onClose, showToast }) {
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  const [copied, setCopied] = useState(false);
  const expiresDate = expiresAt ? new Date(expiresAt) : null;
  const daysLeft = expiresDate ? Math.max(0, Math.ceil((expiresDate.getTime() - Date.now()) / 86400000)) : 7;

  const handleShare = async () => {
    const shareText = `Tritt meinem ${storeName || "Memorica-Bereich"} bei mit dem Code: ${code}`;
    if (navigator.share) {
      try { await navigator.share({ title: "Memorica-Einladung", text: shareText }); return; }
      catch (e) { /* fallthrough */ }
    }
    handleCopy();
  };
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      if (showToast) showToast("Code kopiert");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      if (showToast) showToast("Kopieren fehlgeschlagen", "error");
    }
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 96,
        background: dark ? "rgba(0,0,0,0.6)" : "rgba(0,0,0,0.4)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.2s ease",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 380, radius: 22, padding: "22px 22px 18px", zIndex: 97 })}>
        <ModalHeader t={t} dark={dark} F={F} title="Code generiert" onClose={onClose} marginBottom={20} />
        <div style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", border: `1px solid ${t.glassBorder}`, borderRadius: 14, padding: "20px 16px", marginBottom: 14, textAlign: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: t.text, letterSpacing: "0.10em", fontVariantNumeric: "tabular-nums", fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>{code}</div>
          <div style={{ fontSize: 12, color: t.textMuted, marginTop: 8 }}>Gültig bis: in {daysLeft} {daysLeft === 1 ? "Tag" : "Tagen"}</div>
        </div>
        <div style={{ fontSize: 13, color: t.textSub, lineHeight: 1.5, marginBottom: 16, textAlign: "center" }}>Teile diesen Code mit der Person, die deinem Bereich beitreten soll.</div>
        <button onClick={handleShare} style={{ width: "100%", padding: "13px", background: t.accent.blue.solid, color: "#fff", border: "none", borderRadius: 12, fontFamily: F, fontWeight: 700, fontSize: 14, cursor: "pointer", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/>
          </svg>
          Code teilen
        </button>
        <button onClick={handleCopy} style={{ width: "100%", padding: "11px", background: "transparent", border: `1px solid ${t.glassBorder}`, borderRadius: 12, color: t.text, fontFamily: F, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
          {copied ? "Code kopiert ✓" : "Code kopieren"}
        </button>
      </div>
    </>
  );
}

export function ConfirmDialog({ dark, t, title, message, confirmLabel, confirmDestructive, onCancel, onConfirm }) {
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  const [submitting, setSubmitting] = useState(false);
  const accent = confirmDestructive ? t.accent.red : t.accent.blue;
  return (
    <>
      <div onClick={onCancel} style={{ position: "fixed", inset: 0, zIndex: 97, background: dark ? "rgba(0,0,0,0.6)" : "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 340, radius: 22, padding: "22px 22px 16px", zIndex: 98 })}>
        <div style={{ fontSize: 17, fontWeight: 700, color: t.text, marginBottom: 6, textAlign: "center" }}>{title}</div>
        {message && <div style={{ fontSize: 13, color: t.textSub, marginBottom: 18, textAlign: "center", lineHeight: 1.4 }}>{message}</div>}
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onCancel} style={{ flex: 1, padding: "11px", background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)", border: "none", borderRadius: 11, color: t.text, fontFamily: F, fontWeight: 600, fontSize: 14, cursor: "pointer" }}>Abbrechen</button>
          <button onClick={async () => { setSubmitting(true); await onConfirm(); setSubmitting(false); }} disabled={submitting} style={{ flex: 1, padding: "11px", background: accent.solid, color: "#fff", border: "none", borderRadius: 11, fontFamily: F, fontWeight: 700, fontSize: 14, cursor: submitting ? "default" : "pointer", opacity: submitting ? 0.6 : 1 }}>{submitting ? "…" : confirmLabel}</button>
        </div>
      </div>
    </>
  );
}

export function ModeMembersSheet({ isOpen, onClose, store, authToken, dark, t, showToast, onMembersChanged }) {
  const [members, setMembers]         = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [generating, setGenerating]   = useState(false);
  const [showCode, setShowCode]       = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);

  const loadMembersAndInvitations = async () => {
    if (!store?.id || !authToken) return;
    setLoading(true);
    const headers = { "Authorization": `Bearer ${authToken}` };
    try {
      const [mr, ir] = await Promise.all([
        fetch(`/api/stores/${store.id}/members`, { headers }),
        fetch(`/api/stores/${store.id}/invitations`, { headers }),
      ]);
      if (mr.ok) setMembers(await mr.json()); else setMembers([]);
      if (ir.ok) setInvitations(await ir.json()); else setInvitations([]);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { if (isOpen) loadMembersAndInvitations(); }, [isOpen, store?.id]);

  const generateCode = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      const res = await fetch(`/api/stores/${store.id}/invitations`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
        body: JSON.stringify({ role: "fulfillment" }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); showToast(d.reason || "Code konnte nicht generiert werden", "error"); return; }
      const inv = await res.json();
      setShowCode({ code: inv.code, role: inv.role, expires_at: inv.expires_at });
      loadMembersAndInvitations();
    } catch (e) { showToast("Netzwerkfehler", "error"); }
    finally { setGenerating(false); }
  };

  const removeMember = async (member) => {
    try {
      const res = await fetch(`/api/stores/${store.id}/members/${member.user_id}`, { method: "DELETE", headers: { "Authorization": `Bearer ${authToken}` } });
      if (!res.ok) { const d = await res.json().catch(() => ({})); showToast(d.reason || "Entfernen fehlgeschlagen", "error"); return; }
      showToast("Mitglied entfernt");
      loadMembersAndInvitations();
      if (onMembersChanged) onMembersChanged();
    } catch (e) { showToast("Netzwerkfehler", "error"); }
  };

  const revokeInvite = async (invId) => {
    try {
      const res = await fetch(`/api/stores/${store.id}/invitations/${invId}`, { method: "DELETE", headers: { "Authorization": `Bearer ${authToken}` } });
      if (!res.ok) { showToast("Code konnte nicht widerrufen werden", "error"); return; }
      showToast("Code widerrufen");
      loadMembersAndInvitations();
    } catch (e) { showToast("Netzwerkfehler", "error"); }
  };

  return (
    <>
      <BottomSheet isOpen={isOpen} onClose={onClose} title="Mitglieder" subtitle={store?.name} dark={dark} t={t} F={F}>
        <SettingsSectionLabel t={t}>Mitglieder ({members.length})</SettingsSectionLabel>
        <SettingsCard dark={dark} t={t}>
          {members.length === 0 ? (
            <SettingsRow isFirst label={loading ? "Lade…" : "Nur du"} t={t} dark={dark} F={F} />
          ) : members.map((m, i) => (
            <SettingsRow key={m.user_id} isFirst={i === 0} label={m.email || m.user_id} sub={m.role === "owner" ? "Inhaber" : m.role} value={m.role !== "owner" ? "Entfernen" : undefined} onClick={m.role !== "owner" ? () => setConfirmRemove(m) : undefined} destructive={m.role !== "owner"} t={t} dark={dark} F={F} />
          ))}
        </SettingsCard>
        {invitations.length > 0 && (
          <>
            <SettingsSectionLabel t={t}>Aktive Codes ({invitations.length})</SettingsSectionLabel>
            <SettingsCard dark={dark} t={t}>
              {invitations.map((inv, i) => (
                <SettingsRow key={inv.id} isFirst={i === 0} label={inv.code} sub={`Rolle: ${inv.role} · gültig bis ${new Date(inv.expires_at).toLocaleDateString("de-DE")}`} value="Widerrufen" onClick={() => revokeInvite(inv.id)} destructive t={t} dark={dark} F={F} />
              ))}
            </SettingsCard>
          </>
        )}
        <div style={{ padding: "20px 16px 8px" }}>
          <button onClick={generateCode} disabled={generating} style={{ width: "100%", padding: "14px", background: t.accent.blue.solid, color: "#fff", border: "none", borderRadius: 12, fontFamily: F, fontWeight: 700, fontSize: 15, cursor: generating ? "default" : "pointer", opacity: generating ? 0.6 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, boxShadow: `0 4px 14px ${t.accent.blue.weak}` }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
            {generating ? "Erzeuge…" : "Mitglied einladen"}
          </button>
          <div style={{ fontSize: 11, color: t.textSub, marginTop: 8, textAlign: "center", lineHeight: 1.4 }}>Generiert einen Einladungscode, gültig 7 Tage</div>
        </div>
      </BottomSheet>
      {showCode && <InviteCodeSheet dark={dark} t={t} code={showCode.code} expiresAt={showCode.expires_at} storeName={store?.name} onClose={() => setShowCode(null)} showToast={showToast} />}
      {confirmRemove && <ConfirmDialog dark={dark} t={t} title={`${confirmRemove.email || confirmRemove.user_id} entfernen?`} message="Das Mitglied verliert sofort Zugriff auf diesen Bereich." confirmLabel="Entfernen" confirmDestructive onCancel={() => setConfirmRemove(null)} onConfirm={async () => { await removeMember(confirmRemove); setConfirmRemove(null); }} />}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// CSV-Export
// ─────────────────────────────────────────────────────────────

function csvEscape(v) {
  if (v === null || v === undefined) return "";
  const s = typeof v === "object" ? JSON.stringify(v) : String(v);
  if (/[",;\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function rowsToCsv(rows, columns) {
  if (!rows || rows.length === 0) return columns.join(",") + "\n";
  const header = columns.join(",");
  const body = rows.map(r => columns.map(c => csvEscape(r[c])).join(",")).join("\n");
  return header + "\n" + body + "\n";
}

async function fetchStoreCsvFiles(store, authToken) {
  const headers = { "Authorization": `Bearer ${authToken}` };
  const apiBase = `/api/stores/${store.id}`;
  const files = {};
  const safeName = (store.name || "store").replace(/[^a-z0-9]+/gi, "_");
  const prefix = `${safeName}__`;

  if ((store.type || "online") === "online") {
    try { const r = await fetch(`${apiBase}/orders?include_archived=1`, { headers }); if (r.ok) { const orders = await r.json(); files[`${prefix}orders.csv`] = rowsToCsv(orders, ["id","order_number","channel_id","buyer_name","status","price","shipping_cost","fees","tracking_number","carrier","label_key","is_archived","archived_at","created_at","updated_at"]); } } catch (e) {}
    try { const r = await fetch(`${apiBase}/batches`, { headers }); if (r.ok) { const batches = await r.json(); files[`${prefix}batches.csv`] = rowsToCsv(batches, ["id","label","status","starts_at","ends_at","total_revenue","total_fees","created_at"]); } } catch (e) {}
  }

  if (store.type === "flea" || store.type === "market") {
    try { const r = await fetch(`${apiBase}/items?include_inactive=1`, { headers }); if (r.ok) { const items = await r.json(); files[`${prefix}items.csv`] = rowsToCsv(items, ["id","name","description","price","wish_price","quantity","active","created_at","updated_at"]); } } catch (e) {}
    try {
      const r = await fetch(`${apiBase}/sessions?limit=2000`, { headers });
      if (r.ok) {
        const raw = await r.json();
        const sessions = Array.isArray(raw) ? raw : (raw.sessions || []);
        files[`${prefix}sessions.csv`] = rowsToCsv(sessions, ["id","location","starts_at","ends_at","notes","created_at"]);
        const allSales = [], allSaleItems = [];
        await Promise.all(sessions.map(async (s) => {
          try { const sr = await fetch(`${apiBase}/sessions/${s.id}`, { headers }); if (!sr.ok) return; const detail = await sr.json(); for (const sale of (detail.sales || [])) { allSales.push({ ...sale, _location: s.location }); for (const si of (sale.sale_items || [])) allSaleItems.push({ ...si, sale_id: sale.id, session_id: s.id }); } } catch (e) {}
        }));
        if (allSales.length > 0) files[`${prefix}sales.csv`] = rowsToCsv(allSales, ["id","session_id","_location","total","payment","is_bundle","is_voided","voided_at","sold_at","notes"]);
        if (allSaleItems.length > 0) files[`${prefix}sale_items.csv`] = rowsToCsv(allSaleItems, ["id","sale_id","session_id","item_id","item_name","quantity","unit_price","wish_price"]);
      }
    } catch (e) {}
  }
  return files;
}

export async function exportSingleStoreCsv(store, authToken) {
  const files = await fetchStoreCsvFiles(store, authToken);
  if (!window.JSZip) {
    await new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://cdn.jsdelivr.net/npm/jszip@3.10.1/dist/jszip.min.js";
      s.onload = resolve; s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  const zip = new window.JSZip();
  for (const [name, content] of Object.entries(files)) zip.file(name, content);
  const blob = await zip.generateAsync({ type: "blob" });
  const safe = (store.name || "store").replace(/[^a-z0-9]+/gi, "_");
  const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = `${safe}-${ts}.zip`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
